import { useForm, useWatch, useFieldArray } from "react-hook-form";
import { InlineInputField, InlineSelect, InputField, Label } from "../../../components/custom/programs/fields";
import { Col, Form, Nav, Row } from "react-bootstrap";
import { BudgetCapFields, CheckboxField, FlightFields, SelectField } from "./fields";
import {
  API_FIELD_MAPPINGS,
  FREQUENCY_TIME_UNIT_OPTIONS,
  ORDER_ASIN_CONVERSION_TRACKING_DOMAIN_OPTIONS,
  ORDER_BID_OPTIMIZATION_STATEGY_OPTIONS,
  ORDER_FORM_DEFAULTS,
  ORDER_GOAL_KPI_OPTIONS_BY_GOAL,
  ORDER_GOAL_OPTIONS,
  ORDER_STATE_OPTIONS,
} from "./constants";
import {
  castNumbersInFormData,
  formatOrderFormData,
  resolveOrderFormFieldDependencies,
  validateMandatoryOrderFormFields,
} from "./utils";
import { useMemo, useRef } from "react";
import { Button, buttonRecipe } from "../../../components/core/basic/Button";
import { useMutation } from "@tanstack/react-query";
import ConfirmationModal from "../../../components/core/blocks/ConfirmationModal";
import { successToast, errorToast } from "../../../utils/toasts";
import moment from "moment";
import useOnScreen from "../../../hooks/useOnScreen";
import axios from "axios";
import { useAppSelector } from "../../../redux/hooks";
import { Switch } from "../../../components/fields/base/Switch";
import Select from "react-select";
import { css } from "../../../../styled-system/css";
import { token } from "../../../../styled-system/tokens";
import { StringListInput } from "../../../components/fields/base/StringListInput";
import { useSimpleCan } from "../../../config/casl/casl";
import TagsReviewDisplay from "../../../components/core/blocks/TagsReviewDisplay";
import { useGetV4ProductReportByProduct } from "../../perf_reports/api/product/v4ProductReportByProduct";
import { useBlockWindowQuery } from "../../../components/table/useBlockWindowQuery";
import { usePersistedTableState } from "../../../components/table/usePersistedTableState";
import { useServerTable } from "../../../components/table/tableFeatures";
import { createColumnBuilder } from "../../../components/table/columnBuilder";
import { Table } from "../../../components/table/Table";
import { RT8Pagination } from "../../../components/table/Pagination";
import { ProductCell } from "../../merchandising/components/cells/ProductCell";
import { SearchInput } from "../../../components/fields/base/TextField";
import { HStack } from "../../../../styled-system/jsx";
import { useDebounce } from "@reactuses/core";
import { resetTable } from "../../../components/table/utils";
import { MdRotateLeft } from "react-icons/md";
import { useTranslation } from "react-i18next";

const showAutomaticLineItemCreationOptions = false;
const EXCLUDED_ORDER_API_FIELDS = ["conversionTrackingAsins"];
const columnHelper = createColumnBuilder();

const sectionNavClass = css({
  "& .nav-link:not(.active):hover, & .nav-link:not(.active):focus, & .nav-link:not(.active):focus-visible": {
    borderBottomColor: "transparent",
  },
});

const DSPOrderForm = ({
  initialFormData = {},
  createMode = false,
  onCancel = () => {},
  onSuccessfulSubmit = () => {},
  dspAdvertiserId,
  campaignId = null,
  orderEndDate = null,
}) => {
  const { t } = useTranslation(["dsp"]);
  const { marketPlace } = useAppSelector((state) => state.marketPlace);
  const user = useAppSelector((state) => state.user);
  const userCanUpdateDSPEntity = useSimpleCan("update", "DSPEntity");

  const fieldColumnWidth = createMode ? 8 : 4;
  const errorClassNames = createMode ? "d-block ms-3 mt-2 fs-5" : "d-block ms-2 mt-0";
  const entityId = initialFormData?.id;

  const sections = [
    { key: "overview", label: t(($) => $.dsp["Overview"]) },
    { key: "goals", label: t(($) => $.dsp["Goal"]) },
    { key: "delivery", label: t(($) => $.dsp["Delivery"]) },
    { key: "conversionTracking", label: t(($) => $.dsp["Conversion Tracking"]) },
    { key: "targeting", label: t(($) => $.dsp["Targeting & Optimization"]) },
  ];

  const overviewSectionRef = useRef();
  const goalSectionRef = useRef();
  const deliverySectionRef = useRef();
  const conversionTrackingSectionRef = useRef();
  const targetingSectionRef = useRef();
  const scrollContainerRef = useRef(null);

  const visibility = [];
  visibility[0] = useOnScreen(overviewSectionRef);
  visibility[1] = useOnScreen(goalSectionRef);
  visibility[2] = useOnScreen(deliverySectionRef);
  visibility[3] = useOnScreen(conversionTrackingSectionRef);
  visibility[4] = useOnScreen(targetingSectionRef);

  const sectionRefMapping = {
    overview: overviewSectionRef,
    goals: goalSectionRef,
    delivery: deliverySectionRef,
    conversionTracking: conversionTrackingSectionRef,
    targeting: targetingSectionRef,
  };

  const greatestVisibleStep = visibility.lastIndexOf(true);

  const {
    control,
    register,
    setValue,
    reset,
    formState: { errors, dirtyFields },
    setError,
    clearErrors,
  } = useForm({
    mode: "onBlur",
    defaultValues:
      createMode ? { ...ORDER_FORM_DEFAULTS, advertiserId: dspAdvertiserId } : formatOrderFormData(initialFormData),
  });

  const formState = useWatch({ control });

  const asinFilter = useMemo(
    () =>
      formState.conversionTrackingAsins?.length ?
        [{ id: "asin", value: { op: "anyof", value: formState.conversionTrackingAsins.map((p) => p.productId) } }]
      : [],
    [formState.conversionTrackingAsins],
  );

  const {
    state: tableState,
    initialState: tableInitialState,
    setters: tableSetters,
  } = usePersistedTableState({
    persistKey: "dsp-order-products",
    initialState: {
      pagination: { pageIndex: 0, pageSize: 10 },
      sorting: [{ id: "total_sales", desc: true }],
    },
  });

  const debounceSearch = useDebounce(tableState.globalFilter, 500);

  const byProductOptionsGetter = useGetV4ProductReportByProduct();
  const productQuery = useBlockWindowQuery(
    byProductOptionsGetter,
    {
      pagination: tableState.pagination,
      sorting: tableState.sorting,
      filters: asinFilter,
      search: debounceSearch,
    },
    { enabled: !!formState.conversionTrackingAsins?.length },
  );

  const productColumns = useMemo(
    () => [
      columnHelper.accessor("product_title", {
        id: "product_title",
        header: t(($) => $.dsp["Product"]),
        size: 250,
        enableHiding: false,
        cell: ({ row }) => (
          <ProductCell
            original={{
              ...row.original,
              product_id: row.original.product_id.toString(),
              pricing_plan__status: row.original.pricing_plan_status,
              price_override_end: row.original.price_override_end ? row.original.price_override_end.slice(0, 10) : "",
              cellContext: "dsp",
            }}
            value={row.original.product_title}
            showInventoryBadge={false}
            className={css({ marginRight: "auto", overflowX: "hidden" })}
          />
        ),
      }),
      columnHelper.metric("sales"),
      columnHelper.metric("units"),
      columnHelper.metric("ad_sales"),
      columnHelper.display({
        id: "domain",
        header: t(($) => $.dsp["Domain"]),
        size: 160,
        cell: ({ row, table }) => {
          const { conversionTrackingAsins, setValue, userCanUpdateDSPEntity } = table.options.meta;
          const asinSpec = conversionTrackingAsins?.find((asin) => asin.productId === row.original.asin);
          return (
            <Select
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: token("zIndex.dropdown") }),
                option: (provided) => ({
                  ...provided,
                  fontSize: "12px",
                }),
                control: (base) => ({
                  ...base,
                  width: "130px",
                }),
              }}
              menuPortalTarget={document.body}
              options={ORDER_ASIN_CONVERSION_TRACKING_DOMAIN_OPTIONS}
              defaultValue={ORDER_ASIN_CONVERSION_TRACKING_DOMAIN_OPTIONS.find(
                (option) => option.value === asinSpec?.domain,
              )}
              onChange={(value) => {
                const updatedDomain = value.value;
                const updatedAsins = conversionTrackingAsins?.map((asin) => {
                  if (asin.productId === row.original.asin) {
                    return { ...asin, domain: updatedDomain };
                  }
                  return asin;
                });
                setValue("conversionTrackingAsins", updatedAsins, {
                  shouldDirty: true,
                });
              }}
              isDisabled={!userCanUpdateDSPEntity}
            />
          );
        },
      }),
      columnHelper.display({
        id: "featured",
        header: t(($) => $.dsp["Featured"]),
        size: 100,
        cell: ({ row, table }) => {
          const { conversionTrackingAsins, setValue, userCanUpdateDSPEntity } = table.options.meta;
          const asinSpec = conversionTrackingAsins?.find((asin) => asin.productId === row.original.asin);
          return (
            <Switch
              isSelected={asinSpec?.productAssociation === "FEATURED"}
              onChange={() => {
                const updatedStatus = asinSpec?.productAssociation === "FEATURED" ? "NOT_FEATURED" : "FEATURED";
                const updatedAsins = conversionTrackingAsins?.map((asin) => {
                  if (asin.productId === row.original.asin) {
                    return { ...asin, productAssociation: updatedStatus };
                  }
                  return asin;
                });
                setValue("conversionTrackingAsins", updatedAsins, {
                  shouldDirty: true,
                });
              }}
              isDisabled={!userCanUpdateDSPEntity}
            />
          );
        },
      }),
    ],
    [t],
  );

  const productsTable = useServerTable({
    data: productQuery.data?.data,
    columns: productColumns,
    getRowId: (row) => row.asin,
    enableRowSelection: true,
    rowCount: productQuery.data?.count ?? 0,
    state: tableState,
    initialState: tableInitialState,
    ...tableSetters,
    meta: {
      showSkeleton: productQuery.isFetching && (!productQuery.data || productQuery.isPlaceholderData),
      conversionTrackingAsins: formState.conversionTrackingAsins,
      setValue,
      userCanUpdateDSPEntity,
    },
  });

  const {
    fields: budgetCapFields,
    append: appendBudgetCap,
    remove: removeBudgetCap,
  } = useFieldArray({
    control,
    name: "budgetCaps",
  });

  const {
    fields: flightFields,
    append: appendFlight,
    remove: removeFlight,
  } = useFieldArray({
    control,
    name: "flights",
  });

  // Below handles cases where form state needs to change in dependent fields
  resolveOrderFormFieldDependencies(formState, budgetCapFields, setValue);

  const updateOrder = useMutation({
    mutationFn: async () => {
      let updateData = {};
      Object.keys(dirtyFields)
        ?.filter((key) => !EXCLUDED_ORDER_API_FIELDS.includes(key))
        .forEach((dirtyFieldKey) => {
          updateData[API_FIELD_MAPPINGS[dirtyFieldKey]] = formState[dirtyFieldKey];
        });

      updateData = castNumbersInFormData(updateData);

      if ("optimization" in updateData && "goalSetting" in updateData.optimization) {
        updateData.optimization.goalSetting = {
          kpi: updateData.optimization.goalSetting.kpi,
          targetKpi: updateData.optimization.goalSetting.targetKpi,
        };
      }

      if ("flights" in updateData) {
        updateData["flights"] = {
          flights: updateData["flights"],
        };
      }

      let res;

      const processOrderUpdates = Object.keys(updateData)?.filter((key) => key !== "conversionTrackingAsins").length;

      if (processOrderUpdates) {
        res = await axios.patch(`/dsp/order/${entityId}/`, updateData);
      }

      if (dirtyFields.conversionTrackingAsins) {
        const trackedProductsData = {
          asins: formState.conversionTrackingAsins,
          campaignId: campaignId,
          marketplace: marketPlace,
        };

        const conversionTrackingProductsRes = await axios.post(
          "/dsp/update_conversion_tracking_products/",
          trackedProductsData,
        );

        // Handle cases where goal type requires tracking products before order can be activated
        if (
          ["RETURN_ON_AD_SPEND", "COST_PER_SIGN_UP"].includes(formState?.optimization?.goalSetting?.kpi) &&
          formState.state === "ACTIVE"
        ) {
          const updateData = {
            state: formState.state,
          };
          const stateUpdateRes = await axios.patch(`/dsp/order/${entityId}/`, updateData);
          return stateUpdateRes;
        }

        return conversionTrackingProductsRes;
      }

      return res;
    },

    onSuccess: () => {
      reset({}, { keepValues: true });
      successToast({
        message: t(($) => $.dsp["Order successfully updated"]),
      });
      onSuccessfulSubmit();
    },

    onError: (error) => {
      errorToast({
        message: t(($) => $.dsp["Failed to update order"]),
        error,
      });
    },
  });

  const createOrder = useMutation({
    mutationFn: async () => {
      const fields = [
        "budgetCaps",
        "comments",
        "optimization",
        "name",
        "frequencies",
        "flights",
        "advertiserId",
        "fees",
      ];

      let createData = {};

      fields.forEach((key) => {
        createData[API_FIELD_MAPPINGS[key]] = formState[key];
      });

      createData = castNumbersInFormData(createData);

      if ("goalSetting" in createData.optimization) {
        createData.optimization.goalSetting = {
          kpi: createData.optimization.goalSetting.kpi,
          targetKpi: createData.optimization.goalSetting.targetKpi,
        };
      }

      createData["flights"] = { flights: createData["flights"] };

      const res = await axios.post(`/dsp/order/`, createData);

      if (formState.conversionTrackingAsins?.length) {
        const newCampaignId = res.data.campaign_id;

        const trackedProductsData = {
          asins: formState.conversionTrackingAsins,
          campaignId: newCampaignId,
          marketplace: marketPlace,
        };

        const conversionTrackingProductsRes = await axios.post(
          "/dsp/create_conversion_tracking_products/",
          trackedProductsData,
        );

        return conversionTrackingProductsRes;
      }

      return;
    },

    onSuccess: () => {
      reset({}, { keepValues: true });
      successToast({
        message: t(($) => $.dsp["Order successfully created"]),
      });
      onSuccessfulSubmit();
    },

    onError: (error) => {
      errorToast({
        message: t(($) => $.dsp["Failed to create order"]),
        error,
      });
    },
  });

  if (
    ["RETURN_ON_AD_SPEND", "COST_PER_SIGN_UP"].includes(formState?.optimization?.goalSetting?.kpi) &&
    !formState?.conversionTrackingAsins?.length &&
    formState.state === "ACTIVE" &&
    !errors.conversionTrackingAsins
  ) {
    setError("conversionTrackingAsins", {
      type: "custom",
      message: t(
        ($) =>
          $.dsp["At least one conversion tracking ASIN is required for campaigns with a goal KPI of ROAS of CPSU."],
      ),
    });
  }

  if (
    ((["RETURN_ON_AD_SPEND", "COST_PER_SIGN_UP"].includes(formState?.optimization?.goalSetting?.kpi) &&
      formState?.conversionTrackingAsins?.length) ||
      !["RETURN_ON_AD_SPEND", "COST_PER_SIGN_UP"].includes(formState?.optimization?.goalSetting?.kpi) ||
      formState.state !== "ACTIVE") &&
    errors.conversionTrackingAsins
  ) {
    clearErrors("conversionTrackingAsins");
  }

  return (
    <Row>
      <Col xs={12} md={createMode ? 3 : 2} style={{ borderRight: "1px solid #ccc" }}>
        <Nav
          variant="underline"
          onSelect={(tabId) => {
            const section = sectionRefMapping[tabId]?.current;
            const container = scrollContainerRef.current;
            if (!section || !container) return;
            container.scrollTo({
              top: section.offsetTop - container.offsetTop,
              behavior: "smooth",
            });
          }}
          activeKey={sections[greatestVisibleStep]?.key ?? "overview"}
          className={`flex-column text-xs-center text-md-start ${sectionNavClass}`}
        >
          {sections.map(({ key, label }) => (
            <Nav.Link eventKey={key} key={key} className="text-xs-center fs-4 text-md-start py-2">
              {label}
            </Nav.Link>
          ))}
        </Nav>
      </Col>
      <Col
        ref={scrollContainerRef}
        xs={12}
        md={createMode ? 9 : 10}
        className={createMode ? "p-5 custom-scroll-container" : "px-5"}
        style={createMode ? { overflowY: "auto", maxHeight: "90vh", position: "relative" } : {}}
      >
        {createMode && (
          <Row>
            <h2 className="fw-bold fs-1 ps-2">{t(($) => $.dsp["Create a New DSP Order"])}</h2>
          </Row>
        )}
        <form
          onSubmit={(e) => {
            e.preventDefault();
          }}
          className={css({ paddingBottom: "xxlarge" })}
        >
          {/* Config */}
          <Row className="py-3 dsp-form-section" ref={overviewSectionRef}>
            <Form.Label className="fw-bold fs-2 px-2">{t(($) => $.dsp["Overview"])}</Form.Label>
            <Row className="py-3">
              <Col xs={12} lg={8} xl={fieldColumnWidth}>
                <InputField
                  label={t(($) => $.dsp["Name"])}
                  placeholder={t(($) => $.dsp["Name"])}
                  fieldName="name"
                  disabled={formState.deliveryStatus === "ENDED" || !userCanUpdateDSPEntity}
                  errors={errors}
                  control={control}
                  rules={{
                    required: { value: true, message: t(($) => $.dsp["Name is required"]) },
                    minLength: { value: 2, message: t(($) => $.dsp["Name must be at least 1 character in length"]) },
                    maxLength: {
                      value: 255,
                      message: t(($) => $.dsp["Name must be 255 characters or less in length"]),
                    },
                  }}
                  errorClassNames={errorClassNames}
                />
              </Col>
            </Row>
            {!createMode ?
              <Row className="py-3">
                <Col xs={12} lg={8} xl={fieldColumnWidth}>
                  <SelectField
                    label={t(($) => $.dsp["State"])}
                    options={ORDER_STATE_OPTIONS}
                    fieldName={"state"}
                    control={control}
                    disabled={!userCanUpdateDSPEntity}
                  />
                </Col>
              </Row>
            : null}

            <Row className="py-3">
              <Col xs={12} lg={8} xl={fieldColumnWidth}>
                <InputField
                  label={
                    <>
                      {t(($) => $.dsp["Comments"])} <small style={{ fontWeight: 400 }}>(optional)</small>
                    </>
                  }
                  fieldName="comments"
                  errors={errors}
                  control={control}
                  rules={{
                    maxLength: {
                      value: 255,
                      message: t(($) => $.dsp["Comments must be 255 characters or less in length"]),
                    },
                  }}
                  errorClassNames={errorClassNames}
                  disabled={!userCanUpdateDSPEntity}
                />
              </Col>
            </Row>
          </Row>

          {/* Goal and Optimization */}
          <Row className="py-3 dsp-form-section" ref={goalSectionRef}>
            <Form.Label className="fw-bold fs-2 px-2">{t(($) => $.dsp["Goal and Optimization"])}</Form.Label>

            <Row className="py-3">
              <Col xs={12} lg={8} xl={fieldColumnWidth}>
                <SelectField
                  label={t(($) => $.dsp["Goal"])}
                  options={ORDER_GOAL_OPTIONS}
                  fieldName={"optimization.goalSetting.goal"}
                  control={control}
                  disabled={!userCanUpdateDSPEntity}
                />
              </Col>
            </Row>
            <Row className="py-3">
              <Col xs={12} lg={8} xl={fieldColumnWidth}>
                <SelectField
                  label={t(($) => $.dsp["KPI"])}
                  options={
                    !formState?.optimization?.goalSetting?.goal ?
                      []
                    : ORDER_GOAL_KPI_OPTIONS_BY_GOAL[formState.optimization?.goalSetting?.goal]
                  }
                  fieldName={"optimization.goalSetting.kpi"}
                  control={control}
                  disabled={!userCanUpdateDSPEntity}
                />
              </Col>
            </Row>
            <Row className="py-3">
              <Col xs={12} lg={fieldColumnWidth} xl={fieldColumnWidth / 2}>
                <InputField
                  label={t(($) => $.dsp["KPI Target"])}
                  placeholder={t(($) => $.dsp["Enter a KPI target"])}
                  fieldName="optimization.goalSetting.targetKpi"
                  errors={errors}
                  control={control}
                  rules={{}}
                  type="number"
                  disabled={formState.optimization?.goalSetting?.disableKpiTarget || !userCanUpdateDSPEntity}
                  errorClassNames={errorClassNames}
                />
              </Col>
              <Col xs={12} lg={fieldColumnWidth} xl={fieldColumnWidth / 2} className="mt-auto pb-3">
                <CheckboxField
                  label={t(($) => $.dsp["Don't set a KPI target"])}
                  fieldName="optimization.goalSetting.disableKpiTarget"
                  control={control}
                  onChange={(value) => {
                    if (value) {
                      setValue("optimization.goalSetting.targetKpi", 0);
                    }
                  }}
                  disabled={!userCanUpdateDSPEntity}
                />
              </Col>
            </Row>
            <Row className="py-3">
              <Col xs={12} lg={8} xl={fieldColumnWidth}>
                <SelectField
                  label={t(($) => $.dsp["Bid Optimization Strategy"])}
                  options={ORDER_BID_OPTIMIZATION_STATEGY_OPTIONS}
                  fieldName={"optimization.bidStrategy"}
                  control={control}
                  disabled={!userCanUpdateDSPEntity}
                />
              </Col>
            </Row>
            <Row className="py-3">
              <Col xs={12} lg={8} xl={fieldColumnWidth}>
                <CheckboxField
                  label={t(($) => $.dsp["Automate Budget Allocation"])}
                  fieldName={"optimization.automateBudgetAllocation"}
                  control={control}
                  disabled={!userCanUpdateDSPEntity}
                />
              </Col>
            </Row>
            {/* TODO: This field isn't visible in the console UI and is still in closed beta, but automatically creates line items */}
            {/* We probably don't want to show it in the update panel yet, but double-check if anyone knows more about it */}
            {createMode && showAutomaticLineItemCreationOptions && (
              <>
                <Col xs={12} lg={fieldColumnWidth} className="mt-auto pb-3">
                  <CheckboxField
                    label={t(($) => $.dsp["Automatically create ad groups based on KPI goal"])}
                    fieldName="enableAutomatedAdGroupCreation"
                    control={control}
                    onChange={(value) => {
                      if (value) {
                        setValue("optimization.automatedAdGroupCreationSettings", {
                          performancePlus: {
                            inventoryTypes: [],
                          },
                        });
                      } else {
                        setValue("optimization.automatedAdGroupCreationSettings", { performancePlus: {} });
                      }
                    }}
                    disabled={!userCanUpdateDSPEntity}
                  />
                </Col>
                {formState.enableAutomatedAdGroupCreation && (
                  <Col xs={12} lg={fieldColumnWidth} className="mt-auto pb-3 d-flex">
                    <CheckboxField
                      label={t(($) => $.dsp["Create video line item"])}
                      fieldName="performancePlusVideoInventoryType"
                      control={control}
                      onChange={(value) => {
                        if (value) {
                          const updatedInventoryTypes =
                            (
                              Array.isArray(
                                formState.optimization?.automatedAdGroupCreationSettings.performancePlus
                                  ?.inventoryTypes,
                              )
                            ) ?
                              formState.optimization?.automatedAdGroupCreationSettings.performancePlus?.inventoryTypes
                            : [];
                          updatedInventoryTypes.push("VIDEO");

                          setValue(
                            "optimization.automatedAdGroupCreationSettings.performancePlus.inventoryTypes",
                            updatedInventoryTypes,
                          );
                        } else {
                          const updatedInventoryTypes =
                            formState.optimization?.automatedAdGroupCreationSettings.performancePlus.inventoryTypes?.filter(
                              (type) => type !== "VIDEO",
                            );

                          setValue(
                            "optimization.automatedAdGroupCreationSettings.performancePlus.inventoryTypes",
                            updatedInventoryTypes,
                          );
                        }
                      }}
                      disabled={!userCanUpdateDSPEntity}
                    />
                    <CheckboxField
                      label={t(($) => $.dsp["Create display line item"])}
                      fieldName="performancePlusDisplayInventoryType"
                      control={control}
                      onChange={(value) => {
                        if (value) {
                          const updatedInventoryTypes =
                            (
                              Array.isArray(
                                formState.optimization?.automatedAdGroupCreationSettings.performancePlus
                                  ?.inventoryTypes,
                              )
                            ) ?
                              formState.optimization?.automatedAdGroupCreationSettings.performancePlus?.inventoryTypes
                            : [];
                          updatedInventoryTypes.push("DISPLAY");

                          setValue(
                            "optimization.automatedAdGroupCreationSettings.performancePlus.inventoryTypes",
                            updatedInventoryTypes,
                          );
                        } else {
                          const updatedInventoryTypes =
                            formState.optimization?.automatedAdGroupCreationSettings.performancePlus.inventoryTypes?.filter(
                              (type) => type !== "DISPLAY",
                            );

                          setValue(
                            "optimization.automatedAdGroupCreationSettings.performancePlus.inventoryTypes",
                            updatedInventoryTypes,
                          );
                        }
                      }}
                      disabled={!userCanUpdateDSPEntity}
                    />
                  </Col>
                )}
              </>
            )}
          </Row>

          <Row className="py-3 dsp-form-section" ref={deliverySectionRef}>
            <Form.Label className="fw-bold fs-2 px-2">{t(($) => $.dsp["Delivery"])}</Form.Label>
            <Row className="p-3">
              <Form.Label className="fw-bold fs-3 px-2">{t(($) => $.dsp["Flights"])}</Form.Label>
              {flightFields.length ?
                flightFields.map((field, index) => {
                  return (
                    <FlightFields
                      key={field.id}
                      control={control}
                      errors={errors}
                      fieldName={`flights.${index}`}
                      register={register}
                      removeFlight={() => {
                        removeFlight(index);
                      }}
                      orderFlights={formState.flights}
                      index={index}
                      createMode={createMode}
                      orderEndDate={orderEndDate}
                      disableFlightFields={!userCanUpdateDSPEntity}
                      {...field}
                    />
                  );
                })
              : <span className={`text-danger ${errorClassNames} ps-0 mb-3`}>
                  {t(($) => $.dsp["At least one flight is required"])}
                </span>
              }
              {userCanUpdateDSPEntity && (
                <span
                  className="fs-5 px-5 trellis-purple w-auto"
                  onClick={() => {
                    const defaultStartDate = moment
                      .max(formState.flights.map((f) => moment(f.endDateTime)))
                      .add(1, "day");
                    const defaultEndDate = moment(defaultStartDate).add(1, "month");
                    appendFlight({
                      startDateTime: defaultStartDate.toISOString(),
                      endDateTime: defaultEndDate.toISOString(),
                      budgetAmount: 100,
                    });
                  }}
                  style={{
                    color: "#c43066",
                    fontWeight: 500,
                    cursor: "pointer",
                  }}
                >
                  {t(($) => $.dsp["Add new flight"])}
                </span>
              )}
            </Row>
            <Row className="py-3">
              <Col xs={12} lg={8} xl={fieldColumnWidth}>
                <Form.Group>
                  <Label>{t(($) => $.dsp["Budget Caps"])}</Label>
                  <CheckboxField
                    label={t(($) => $.dsp["Set a daily or monthly spending limit"])}
                    fieldName={"budgetCapsEnabled"}
                    control={control}
                    onChange={(value) => {
                      if (value) {
                        appendBudgetCap({ amount: 0, recurrenceTimePeriod: "DAILY" });
                      } else {
                        removeBudgetCap();
                      }
                    }}
                    disabled={!userCanUpdateDSPEntity}
                  />
                </Form.Group>
              </Col>
            </Row>

            {budgetCapFields.length ?
              budgetCapFields.map((field, index) => {
                return (
                  <BudgetCapFields
                    key={field.id}
                    control={control}
                    fieldName={`budgetCaps.${index}`}
                    register={register}
                    errors={errors}
                    removeCap={() => {
                      removeBudgetCap(index);
                    }}
                    appendCap={() => {
                      appendBudgetCap({
                        amount: 0,
                        recurrenceTimePeriod:
                          formState.budgetCaps?.[0]?.recurrenceTimePeriod === "DAILY" ? "MONTHLY" : "DAILY",
                      });
                    }}
                    budgetCapCount={budgetCapFields.length}
                    index={index}
                    {...field}
                    createMode={createMode}
                    disableBudgetCapFields={!userCanUpdateDSPEntity}
                  />
                );
              })
            : null}
          </Row>
          {user.is_staff ?
            <>
              <Row className="py-3">
                <Col xs={12} lg={8} xl={fieldColumnWidth}>
                  <Form.Group>
                    <Label>{t(($) => $.dsp["Agency Fees"])}</Label>
                    <CheckboxField
                      label={t(($) => $.dsp["Apply an agency fee"])}
                      fieldName={"agencyFeeEnabled"}
                      control={control}
                      onChange={(value) => {
                        if (!value) {
                          setValue("fees.agencyFeePercentage", 0, { shouldDirty: true });
                        }
                      }}
                      disabled={!createMode || !userCanUpdateDSPEntity}
                    />
                  </Form.Group>
                </Col>
              </Row>
              {formState.agencyFeeEnabled ?
                <Row>
                  <Col xs={12} lg={createMode ? 3 : 2}>
                    <InputField
                      label={t(($) => $.dsp["Agency Fee Percent"])}
                      placeholder="%"
                      fieldName="fees.agencyFeePercentage"
                      disabled={!createMode || !userCanUpdateDSPEntity}
                      errors={errors}
                      control={control}
                      rules={{
                        min: { value: 0, message: t(($) => $.dsp["Agency fee percent must be at least 0"]) },
                        max: { value: 99, message: t(($) => $.dsp["Agency fee percent cannot be greater than 99"]) },
                      }}
                      errorClassNames={errorClassNames}
                    />
                  </Col>
                </Row>
              : null}
            </>
          : null}
          {/* Conversion Tracking */}
          <Row className="py-3 dsp-form-section" ref={conversionTrackingSectionRef}>
            <Form.Label className="fw-bold fs-2 px-2 pb-3">{t(($) => $.dsp["Conversion Tracking"])}</Form.Label>
            {errors.conversionTrackingAsins ?
              <span className={`text-danger ${css({ marginBottom: "medium", textWrap: "nowrap", fontSize: "12px" })}`}>
                {errors.conversionTrackingAsins.message}
              </span>
            : null}
            <Col xs={12} lg={8} xl={fieldColumnWidth} className="pb-5">
              <SelectField
                label={t(($) => $.dsp["Amazon Domain"])}
                options={ORDER_ASIN_CONVERSION_TRACKING_DOMAIN_OPTIONS}
                fieldName={"conversionTrackingDomain"}
                control={control}
                disabled={!userCanUpdateDSPEntity}
              />
            </Col>
            <Label>{t(($) => $.dsp["Products (optional)"])}</Label>
            <div style={{ overflow: "auto" }}>
              {userCanUpdateDSPEntity ?
                <StringListInput
                  value={formState.conversionTrackingAsins?.map((asin) => asin.productId) ?? []}
                  onChange={(asins) => {
                    if (asins?.length > formState.conversionTrackingAsins?.length) {
                      const newAsins = asins
                        ?.map((asin) => asin.trim())
                        ?.filter(
                          (asin) =>
                            !formState.conversionTrackingAsins
                              ?.map((trackingSpec) => trackingSpec.productId)
                              ?.includes(asin),
                        )
                        ?.filter((newAsin) =>
                          newAsin
                            ?.toString()
                            .toLowerCase()
                            .match(/b0[a-z0-9]{8}/i),
                        );

                      const formattedNewAsins =
                        newAsins?.map((newAsin) => ({
                          productId: newAsin,
                          productAssociation: "FEATURED",
                          domain: formState.conversionTrackingDomain,
                        })) ?? [];
                      // Handle adding ASIN
                      const formattedAsins = [...formState.conversionTrackingAsins, ...formattedNewAsins];
                      setValue("conversionTrackingAsins", formattedAsins, { shouldDirty: true });
                    } else {
                      // Handle removing ASIN
                      const updatedAsins =
                        asins?.length ?
                          formState.conversionTrackingAsins?.filter((asin) => asins?.includes(asin.productId))
                        : [];
                      setValue("conversionTrackingAsins", updatedAsins, {
                        shouldDirty: true,
                      });
                    }
                  }}
                  placeholder={t(($) => $.dsp["Enter ASINs..."])}
                  css={{ paddingBottom: "large" }}
                />
              : <TagsReviewDisplay tags={formState.conversionTrackingAsins?.map((asin) => asin.productId)} />}
            </div>

            {formState.conversionTrackingAsins?.length ?
              <Col xs={12} className="pb-5">
                <HStack padding={"small"} fontSize={"large"}>
                  <Button
                    variant={"contentOnly"}
                    onPress={() => {
                      resetTable(productsTable);
                    }}
                    fontSize={"xxlarge"}
                  >
                    <MdRotateLeft />
                  </Button>
                  <SearchInput
                    aria-label={t(($) => $.dsp["Search"])}
                    isExpandable
                    css={{ flex: "0 1 350px" }}
                    value={productsTable.state.globalFilter}
                    onChange={productsTable.setGlobalFilter}
                  />
                  <RT8Pagination className={css({ marginLeft: "auto" })} table={productsTable} />
                </HStack>
                <Table table={productsTable} className={css({ minWidth: "100%" })} />
              </Col>
            : null}
          </Row>

          {/* Targeting */}
          <Row className="py-3 dsp-form-section" ref={targetingSectionRef}>
            <Form.Label className="fw-bold fs-2 px-2">{t(($) => $.dsp["Targeting"])}</Form.Label>
            <Row className="py-3">
              <Col xs={12} lg={8} xl={fieldColumnWidth}>
                <Form.Group>
                  <Label>{t(($) => $.dsp["Frequencies"])}</Label>
                  <CheckboxField
                    label={t(($) => $.dsp["Limit the number of times a shopper can view this ad"])}
                    fieldName={"frequencies"}
                    deriveFieldValue={() => {
                      return formState.frequencies?.[0].frequencyType === "CUSTOM";
                    }}
                    control={control}
                    onChange={(value) => {
                      if (value) {
                        setValue("frequencies.0", {
                          frequencyType: "CUSTOM",
                          maxImpressions: 4,
                          timeUnitCount: 1,
                          timeUnit: FREQUENCY_TIME_UNIT_OPTIONS[0].value,
                        });
                      } else {
                        setValue("frequencies.0.frequencyType", "UNCAPPED");
                      }
                    }}
                    disabled={!userCanUpdateDSPEntity}
                  />
                </Form.Group>
              </Col>
            </Row>
            {formState.frequencies?.[0]?.frequencyType !== "UNCAPPED" && (
              <Row className="p-3">
                <Col xs={12} lg={8} xl={createMode ? 8 : 5} className="d-flex align-items-center gap-3 fs-5">
                  {t(($) => $.dsp["No more than"])}{" "}
                  <InlineInputField
                    fieldName={`frequencies.0.maxImpressions`}
                    control={control}
                    rules={{
                      required: formState.frequencyCapped,
                      min: {
                        value: 1,
                        message: t(($) => $.dsp["Max impressions must be at least 1"]),
                      },
                      max: {
                        value: 99000,
                        message: t(($) => $.dsp["Max impressions cannot be greater than 99000"]),
                      },
                    }}
                    width="125px"
                    errors={errors}
                    placeholder={null}
                    type={"number"}
                    disabled={!userCanUpdateDSPEntity}
                  />{" "}
                  {t(($) => $.dsp["times in"])}{" "}
                  <InlineInputField
                    fieldName={`frequencies.0.timeUnitCount`}
                    control={control}
                    rules={{
                      required: formState.frequencyCapped,
                      min: {
                        value: 1,
                        message: t(($) => $.dsp["Count must be at least 1"]),
                      },
                      max: {
                        value: 60,
                        message: t(($) => $.dsp["Count cannot be greater than 60"]),
                      },
                    }}
                    width="125px"
                    errors={errors}
                    placeholder={null}
                    type={"number"}
                    disabled={!userCanUpdateDSPEntity}
                  />{" "}
                  <InlineSelect
                    options={FREQUENCY_TIME_UNIT_OPTIONS}
                    fieldName={`frequencies.0.timeUnit`}
                    control={control}
                    rules={{ required: true }}
                    disabled={!userCanUpdateDSPEntity}
                  />
                </Col>
              </Row>
            )}
          </Row>
          <Row className="pt-4">
            <Col
              xs={12}
              lg={createMode ? 12 : 8}
              xl={12}
              className={`d-flex align-items-center justify-content-center gap-5 fs-5 ${createMode ? "pb-5 mb-5" : ""}`}
            >
              {createMode ?
                <Button
                  variant="secondary"
                  onPress={() => {
                    onCancel();
                    reset(formatOrderFormData(initialFormData));
                  }}
                  isDisabled={!userCanUpdateDSPEntity}
                >
                  {t(($) => $.dsp["Cancel"])}
                </Button>
              : <ConfirmationModal
                  title={t(($) => $.dsp["Revert Configuration Changes"])}
                  confirmButtonText={t(($) => $.dsp["Confirm"])}
                  confirmationMessage={
                    <p className="fs-4 w-75 mx-auto">
                      {t(($) => $.dsp["Are you sure you want to revert all changes made to this line item?"])}
                    </p>
                  }
                  onConfirm={() => {
                    onCancel();
                    reset(formatOrderFormData(initialFormData));
                  }}
                  inlineModal={true}
                  modalButtonText={
                    <button className={buttonRecipe({ variant: "secondary" })}>{t(($) => $.dsp["Cancel"])}</button>
                  }
                  inlineButtonContainerStyles={{}}
                  disableConfirmButton={!userCanUpdateDSPEntity}
                />
              }

              <Button
                onClick={() => {
                  if (createMode) {
                    createOrder.mutate();
                  } else {
                    updateOrder.mutate();
                  }
                }}
                isDisabled={
                  Object.keys(errors).length || !validateMandatoryOrderFormFields(formState) || !userCanUpdateDSPEntity
                }
                isPending={updateOrder.isPending || createOrder.isPending}
              >
                {createMode ? t(($) => $.dsp["Create"]) : t(($) => $.dsp["Update"])}
              </Button>
            </Col>
          </Row>
        </form>
      </Col>
    </Row>
  );
};

export default DSPOrderForm;
